import {
  Box,
  Tabs,
  Tab,
  Typography,
  List,
  ListItem,
  Avatar,
  TextField,
  IconButton,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import {
  ShoppingCart as CartIcon,
  Group as GroupIcon,
  Payment as PaymentIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Loyalty as VoucherIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { searchUserByEmail, createUser } from "../../api/users";

const FOOTER_HEIGHT = 350;

export default function CartTabs({
  tabValue,
  setTabValue,
  products,
  customer,
  setCustomer,
  note,
  setNote,
  subtotal,
  handleQuantityChange,
  paymentMethod,
  setPaymentMethod,
}) {
  const totalLines = products.length;
  const totalQty = products.reduce((s, it) => s + (it.quantity || 0), 0);

  // 🔍 Tìm hoặc tạo khách hàng
  // 🔍 Tìm hoặc tạo khách hàng
  const handleSearchCustomer = async () => {
    if (!customer.searchKey) return;

    try {
      const result = await searchUserByEmail(customer.searchKey);

      if (result.error) {
        alert(result.error);
        return;
      }

      let user = null;

      if (result.exists) {
        // ✅ User đã có trong DB
        user = result.data;
      } else {
        // ❌ User chưa có → tạo mới
        const username = customer.searchKey.includes("@")
          ? customer.searchKey.split("@")[0]
          : customer.searchKey;

        const newUser = {
          username,
          email: customer.searchKey.includes("@") ? customer.searchKey : "",
          phone: customer.phone || "",
          address: customer.address || "",
          dateOfBirth: null,
          gender: 0,
        };

        user = await createUser(newUser);
        alert("✅ Tạo user mới thành công!");
      }

      if (user) {
        setCustomer({
          ...customer,
          userId: user.userId, // 👈 gán luôn ID
          name: user.username || "",
          phone: user.phone || "",
          email: user.email || "",
          address: user.address || "",
          loyaltyPoints: user.pointAccumulated || 0,
        });
      }
    } catch (err) {
      console.error("❌ Lỗi xử lý khách hàng:", err);
      alert("Có lỗi khi tìm/tạo khách hàng!");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Tabs header */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 5,
          bgcolor: "#fff",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_e, v) => setTabValue(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab icon={<CartIcon />} iconPosition="start" label="Giỏ hàng" />
          <Tab icon={<GroupIcon />} iconPosition="start" label="Khách hàng" />
          <Tab icon={<PaymentIcon />} iconPosition="start" label="Thanh toán" />
        </Tabs>
      </Box>

      {/* Nội dung các tab */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          pb: { xs: `${FOOTER_HEIGHT + 22}px`, md: `${FOOTER_HEIGHT + 30}px` },
        }}
      >
        {/* TAB GIỎ HÀNG */}
        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                mb: 2,
                gap: 1,
              }}
            >
              <CartIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Giỏ hàng
              </Typography>
              <Box
                sx={{ ml: "auto", display: "flex", gap: 1, flexWrap: "wrap" }}
              >
                <Chip size="small" label={`Dòng: ${totalLines}`} />
                <Chip size="small" label={`SL: ${totalQty}`} />
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Tạm tính: ${subtotal.toLocaleString("vi-VN")}₫`}
                />
              </Box>
            </Box>

            {products.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CartIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography color="text.secondary">
                  Chưa có sản phẩm trong giỏ hàng
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {products.map((p) => (
                  <ListItem
                    key={p.key}
                    sx={{
                      py: 2,
                      px: 0,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: { xs: 1.5, sm: 2 },
                      borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    }}
                  >
                    <Avatar
                      src={p.image}
                      variant="rounded"
                      sx={{
                        width: { xs: 56, sm: 64 },
                        height: { xs: 56, sm: 64 },
                        flexShrink: 0,
                      }}
                    >
                      {p.name?.charAt(0)}
                    </Avatar>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        noWrap
                        sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                      >
                        {p.name}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          flexWrap: "wrap",
                          mt: 0.5,
                        }}
                      >
                        {p.sku && <Chip size="small" label={`SKU: ${p.sku}`} />}
                        {p.color && (
                          <Chip size="small" label={`Màu: ${p.color}`} />
                        )}
                        {p.size && (
                          <Chip size="small" label={`Size: ${p.size}`} />
                        )}
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={`Còn: ${p.stock}`}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          fontSize: { xs: "0.8rem", sm: "0.9rem" },
                        }}
                      >
                        {p.price.toLocaleString("vi-VN")}₫ / 1
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleQuantityChange(p.key, p.quantity - 1)
                          }
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>

                        <TextField
                          value={p.quantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              p.key,
                              parseInt(e.target.value) || 0
                            )
                          }
                          type="number"
                          inputProps={{
                            min: 1,
                            max: p.stock,
                            style: { textAlign: "center", width: 48 },
                          }}
                          size="small"
                          sx={{ mx: 0.5 }}
                        />

                        <IconButton
                          size="small"
                          onClick={() =>
                            handleQuantityChange(p.key, p.quantity + 1)
                          }
                          disabled={p.quantity >= p.stock}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Typography
                        fontWeight="bold"
                        sx={{
                          minWidth: { xs: "auto", sm: 100 },
                          fontSize: { xs: "0.9rem", sm: "1rem" },
                          textAlign: "right",
                        }}
                      >
                        {(p.price * p.quantity).toLocaleString("vi-VN")}₫
                      </Typography>

                      <IconButton
                        onClick={() => handleQuantityChange(p.key, 0)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* TAB KHÁCH HÀNG */}
        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <GroupIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                Thông tin khách hàng
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Nhập số điện thoại hoặc email"
                value={customer.searchKey || ""}
                onChange={(e) =>
                  setCustomer({ ...customer, searchKey: e.target.value })
                }
              />
              <IconButton color="primary" onClick={handleSearchCustomer}>
                <SearchIcon />
              </IconButton>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Họ và tên"
                  value={customer.name || ""}
                  onChange={(e) =>
                    setCustomer({ ...customer, name: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Số điện thoại"
                  value={customer.phone || ""}
                  onChange={(e) =>
                    setCustomer({ ...customer, phone: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={customer.email || ""}
                  onChange={(e) =>
                    setCustomer({ ...customer, email: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Địa chỉ"
                  value={customer.address || ""}
                  multiline
                  rows={2}
                  onChange={(e) =>
                    setCustomer({ ...customer, address: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Điểm tích lũy"
                  value={customer.loyaltyPoints || 0}
                  onChange={(e) =>
                    setCustomer({ ...customer, loyaltyPoints: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VoucherIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* TAB THANH TOÁN */}
        {tabValue === 2 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <PaymentIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                Thanh toán
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Phương thức thanh toán</InputLabel>
                  <Select
                    value={paymentMethod}
                    label="Phương thức thanh toán"
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <MenuItem value="cash">Tiền mặt</MenuItem>
                    <MenuItem value="transfer">Chuyển khoản</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ghi chú đơn hàng"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
}
